import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Button, Card, CardSubtitle, CardTitle, Field, Input } from '@keobook/ui';
import { Layout } from '../components/Layout';
import { ApiError, api, type ReferralMe, type ReferralAgreementTemplate } from '../lib/api';
import { useAuth } from '../lib/auth';

const usd = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  converted: 'Active',
  churned: 'Cancelled',
};

export function Referral() {
  // undefined = loading, null = not a partner yet, object = enrolled
  const [me, setMe] = useState<ReferralMe | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    // Returning from Stripe Connect onboarding (return_url = /referral?connect=…):
    // reconcile payout status before loading, then clean the URL.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('connect')) {
        await api.refreshReferralConnect().catch(() => undefined);
        window.history.replaceState({}, '', '/referral');
      }
    } catch {
      /* ignore */
    }
    try {
      setMe(await api.getReferralMe());
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setMe(null); // not enrolled yet
      } else {
        setError('Could not load your referral dashboard. Please try again.');
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Layout>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        {error && (
          <Card>
            <p role="alert" className="kb-form-error">
              {error}
            </p>
          </Card>
        )}
        {me === undefined && !error && <Card>Loading…</Card>}
        {me === null && <EnrollView onDone={load} />}
        {me && !me.partner.agreementAccepted && <AgreementView onDone={load} />}
        {me && me.partner.agreementAccepted && <Dashboard me={me} onReload={load} />}
      </div>
    </Layout>
  );
}

// --- Not a partner yet: enroll ---------------------------------------------
function EnrollView({ onDone }: { onDone: () => Promise<void> }) {
  const { user } = useAuth();
  const [displayName, setDisplayName] = useState(user?.fullName ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (displayName.trim().length < 2) {
      setError('Please enter a name of at least 2 characters.');
      return;
    }
    setSubmitting(true);
    try {
      await api.enrollReferral(displayName.trim());
      await onDone(); // now enrolled → agreement step
    } catch {
      setError('Could not enroll. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card size="xl">
      <CardTitle>Become a KEOBOOKS referral partner</CardTitle>
      <CardSubtitle>
        Earn a commission on every customer you refer who becomes a paying subscriber — 5% on
        Starter, 10% on Professional+, and 15% on Business+, for as long as they stay.
      </CardSubtitle>
      <form onSubmit={onSubmit} noValidate style={{ marginTop: '1rem' }}>
        <Field label="Partner name" required hint="Your name, or your company / organization.">
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
            placeholder="Acme Advisors"
            required
          />
        </Field>
        {error && (
          <p role="alert" className="kb-form-error">
            {error}
          </p>
        )}
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Enrolling…' : 'Continue'}
        </Button>
      </form>
    </Card>
  );
}

// --- Enrolled but unsigned: present + sign the agreement --------------------
function AgreementView({ onDone }: { onDone: () => Promise<void> }) {
  const [tpl, setTpl] = useState<ReferralAgreementTemplate | null>(null);
  const [signature, setSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getReferralAgreementTemplate()
      .then(setTpl)
      .catch(() => setError('Could not load the agreement. Please refresh.'));
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!tpl) return;
    if (signature.trim().length < 2) {
      setError('Type your full legal name to sign.');
      return;
    }
    setSubmitting(true);
    try {
      await api.acceptReferralAgreement(tpl.version, signature.trim());
      await onDone(); // → active dashboard
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('The agreement was updated. Please refresh and review the new version.');
      } else {
        setError('Could not record your acceptance. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card size="xl">
      <CardTitle>{tpl?.title ?? 'Referral Partner Agreement'}</CardTitle>
      <CardSubtitle>Please read and sign to activate your referral code.</CardSubtitle>
      <div
        style={{
          maxHeight: 360,
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          fontSize: 13,
          lineHeight: 1.55,
          color: '#33322e',
          border: '1px solid #e6e4da',
          borderRadius: 8,
          padding: '1rem',
          margin: '1rem 0',
          background: '#fcfbf7',
        }}
      >
        {tpl ? tpl.body : 'Loading…'}
      </div>
      <form onSubmit={onSubmit} noValidate>
        <Field label="Type your full legal name to sign" required>
          <Input
            value={signature}
            onChange={(e) => setSignature(e.currentTarget.value)}
            placeholder="Jane Q. Partner"
            required
          />
        </Field>
        {error && (
          <p role="alert" className="kb-form-error">
            {error}
          </p>
        )}
        <Button type="submit" disabled={submitting || !tpl}>
          {submitting ? 'Signing…' : 'I Agree — activate my code'}
        </Button>
      </form>
    </Card>
  );
}

// --- Active partner dashboard ----------------------------------------------
function Dashboard({ me, onReload }: { me: ReferralMe; onReload: () => Promise<void> }) {
  const [copied, setCopied] = useState(false);
  const { partner, summary, referrals } = me;

  async function copy() {
    try {
      await navigator.clipboard.writeText(partner.shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the link is still visible to copy manually */
    }
  }

  return (
    <>
      <Card size="xl">
        <CardTitle>Your referral link</CardTitle>
        <CardSubtitle>
          Share this link. When someone signs up through it and becomes a paying customer, you earn
          commission — starting 60 days after their signup, for as long as they stay.
        </CardSubtitle>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: '0.75rem' }}>
          <Input readOnly value={partner.shareLink} onFocus={(e) => e.currentTarget.select()} />
          <Button type="button" onClick={copy}>
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </div>
        <p style={{ fontSize: 12, color: '#6e6c66', marginTop: 8 }}>
          Your code: <strong>{partner.code}</strong>
        </p>
      </Card>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12,
          margin: '1rem 0',
        }}
      >
        <Stat label="Referrals" value={String(summary.totalReferrals)} />
        <Stat label="Active" value={String(summary.converted)} />
        <Stat label="Pending" value={String(summary.pending)} />
        <Stat label="Earned (paid)" value={usd(summary.earningsCents.paid)} />
        <Stat
          label="Pending payout"
          value={usd(summary.earningsCents.accrued + summary.earningsCents.approved)}
        />
      </div>

      <PayoutsCard me={me} />

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <CardTitle>Your referrals</CardTitle>
          <Button type="button" variant="ghost" onClick={() => void onReload()}>
            Refresh
          </Button>
        </div>
        {referrals.length === 0 ? (
          <p style={{ color: '#6e6c66', marginTop: 8 }}>
            No referrals yet. Share your link to get started.
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12, fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#6e6c66', fontSize: 12 }}>
                <th style={{ padding: '6px 8px' }}>Referred</th>
                <th style={{ padding: '6px 8px' }}>Status</th>
                <th style={{ padding: '6px 8px' }}>Became active</th>
              </tr>
            </thead>
            <tbody>
              {referrals.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid #eceadf' }}>
                  <td style={{ padding: '8px' }}>
                    {new Date(r.attributedAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '8px' }}>{STATUS_LABEL[r.status] ?? r.status}</td>
                  <td style={{ padding: '8px' }}>
                    {r.convertedAt ? new Date(r.convertedAt).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p style={{ fontSize: 12, color: '#6e6c66', marginTop: 12 }}>
          To protect customer privacy, we show referral status and dates rather than customer
          details. Payouts are made by bank deposit; we&apos;ll reach out to set that up.
        </p>
      </Card>
    </>
  );
}

function PayoutsCard({ me }: { me: ReferralMe }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { partner, payouts } = me;

  async function setup() {
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.startReferralPayoutOnboarding();
      window.location.href = url; // hand off to Stripe onboarding
    } catch {
      setError('Could not start payout setup. Please try again.');
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardTitle>Payouts</CardTitle>
      {partner.payoutsEnabled ? (
        <CardSubtitle>
          Bank deposit is set up. Commissions are paid automatically once your balance reaches the
          minimum.
        </CardSubtitle>
      ) : (
        <>
          <CardSubtitle>
            Set up bank deposit to get paid. We use Stripe to collect your bank details and tax info
            (W-9) securely.
          </CardSubtitle>
          <div style={{ marginTop: '0.75rem' }}>
            <Button type="button" onClick={setup} disabled={busy}>
              {busy ? 'Opening…' : 'Set up bank payouts'}
            </Button>
          </div>
          {error && (
            <p role="alert" className="kb-form-error">
              {error}
            </p>
          )}
        </>
      )}
      {payouts.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14, fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#6e6c66', fontSize: 12 }}>
              <th style={{ padding: '6px 8px' }}>Date</th>
              <th style={{ padding: '6px 8px' }}>Amount</th>
              <th style={{ padding: '6px 8px' }}>Referrals</th>
              <th style={{ padding: '6px 8px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {payouts.map((p) => (
              <tr key={p.id} style={{ borderTop: '1px solid #eceadf' }}>
                <td style={{ padding: '8px' }}>
                  {new Date(p.paidAt ?? p.createdAt).toLocaleDateString()}
                </td>
                <td style={{ padding: '8px' }}>{usd(p.totalCents)}</td>
                <td style={{ padding: '8px' }}>{p.commissionCount}</td>
                <td style={{ padding: '8px', textTransform: 'capitalize' }}>{p.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <div style={{ fontSize: 12, color: '#6e6c66', textTransform: 'uppercase', letterSpacing: 1 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 600, color: '#1e3a5f', marginTop: 4 }}>{value}</div>
    </Card>
  );
}
